import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
	Tabs,
	Tab,
	Typography,
	Tooltip,
	IconButton,
} from "@mui/material";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";

import DropdownController from "../../components/inputs/DropdownController";
import DatePickerController from "../../components/inputs/DatePickerController";
import PATCloseButton from "../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../components/Buttons/PATSaveButton";
import {
	getCode,
	isValidDateTest,
	maxLengthMessage,
} from "../../utils/functions";
import PATAddButton from "../../components/Buttons/PATAddButton";
import TextFieldController from "../../components/inputs/TextFieldController";
import { AssignmentReturn } from "@mui/icons-material";
import { notify } from "../../reducers/NotifierReducer";
import { getService } from "../../reducers/service";
import { entityNamesForCustomId, serviceNames } from "../../utils/constants";
import { RegularExpressions } from "../../utils/regular-expressions";

const vehicleSchema = yup.object().shape({
	// Vehicle Info
	vehicleNum: yup
		.string()
		.trim()
		.matches(RegularExpressions.ID, "Invalid Vehicle#")
		.max(30, maxLengthMessage(30)),
	unitNumber: yup
		.string()
		.trim()
		.max(30, maxLengthMessage(30))
		.required("Unit number is a required field."),
	inventoryNumber: yup
		.string()
		.trim()
		.max(50, maxLengthMessage(50))
		.required("Inventory number is a required field."),
	status: yup
		.string()
		.trim()
		.max(50, maxLengthMessage(50))
		.required("Status is a required field."),
	vin: yup.string().trim().max(17, maxLengthMessage(17)).nullable(),
	make: yup.string().trim().max(50, maxLengthMessage(50)).nullable(),
	model: yup.string().trim().max(50, maxLengthMessage(50)).nullable(),
	year: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	division: yup.string().trim().max(50, maxLengthMessage(50)).nullable(),
	perPersonnelId: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	notes: yup.string().trim().max(500, maxLengthMessage(500)).nullable(),

	// Mileage
	odometer: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	lastMileage: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	startingMileage: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),

	// Acquisition
	purchaseDate: isValidDateTest("Purchase Date"),
	originalCost: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	purchasedFrom: yup
		.string()
		.trim()
		.max(100, maxLengthMessage(100))
		.nullable(),
	fundingSourceId: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),

	// Registration
	licenseNumber: yup.string().trim().max(30, maxLengthMessage(30)).nullable(),
	licenseExpiration: yup.string().nullable(),
});

const TabPanel = ({ children, value, index }) =>
	value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;

const AddEditVehicleDialog = ({
	open,
	onClose,
	onSave,
	initialData,
	perPersonnelId,
	isLoadData,
	viewOnly = false,
	statusOptions,
	divisionOptions,
	employeeOptions,
	fundingSourceOptions,
	serviceRequirements,
}) => {
	const dispatch = useDispatch();
	const user = useSelector((state) => state.user);
	const userAgency = useSelector((state) => state.user.userAgency);

	const [tab, setTab] = useState(0);

	const buildDefaults = (initialData, perPersonnelId = null) => {
		return {
			vehicleNum: initialData?.vehicleNum || "",
			unitNumber: initialData?.unitNumber || "",
			inventoryNumber: initialData?.inventoryNumber || "",
			status: initialData?.status || "",
			vin: initialData?.vin || "",
			make: initialData?.make || "",
			model: initialData?.model || "",
			year: initialData?.year || "",
			division: initialData?.division || "",
			perPersonnelId: initialData?.perPersonnelId
				? String(initialData.perPersonnelId)
				: perPersonnelId
					? String(perPersonnelId)
					: "",
			notes: initialData?.notes || "",

			odometer: initialData?.odometer || "",
			lastMileage: initialData?.lastMileage || "",
			startingMileage: initialData?.startingMileage || "",

			purchaseDate: initialData?.purchaseDate
				? moment(initialData.purchaseDate).format("MM/DD/YYYY")
				: "",
			originalCost: initialData?.originalCost || "",
			purchasedFrom: initialData?.purchasedFrom || "",
			fundingSourceId: initialData?.fundingSourceId || 0,

			licenseNumber: initialData?.licenseNumber || "",
			licenseExpiration: initialData?.licenseExpiration || "",

			serviceRequirements:
				initialData?.serviceRequirements?.map((sr) =>
					getCode(sr, serviceRequirements),
				) || [],
		};
	};

	const {
		control,
		handleSubmit,
		reset,
		watch,
		setValue,
		formState: { errors, isValid },
	} = useForm({
		resolver: yupResolver(vehicleSchema),
		defaultValues: buildDefaults(initialData, perPersonnelId),
		mode: "onChange",
	});

	// Reset form when dialog opens with new data
	useEffect(() => {
		reset(buildDefaults(initialData, perPersonnelId));
		setTab(0);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialData, reset, isLoadData]);

	// Reset on close
	useEffect(() => {
		if (!open) {
			reset();
			setTab(0);
		}
	}, [open, reset]);

	const onSubmit = async (data) => {
		let vehcleGeneratedId;
		if (!data.vehicleNum?.trim()) {
			vehcleGeneratedId = await generateCustomId();
		}

		const payload = {
			vehicleNum: vehcleGeneratedId
				? vehcleGeneratedId
				: data.vehicleNum?.trim() || null,
			unitNumber: data.unitNumber?.trim() || null,
			inventoryNumber: data.inventoryNumber?.trim() || null,
			status: data.status || null,
			vin: data.vin?.trim() || null,
			make: data.make?.trim() || null,
			model: data.model?.trim() || null,
			year: data.year || null,
			division: data.division || null,
			perPersonnelId: parseInt(data.perPersonnelId) || null,
			notes: data.notes?.trim() || null,

			odometer: data.odometer || null,
			lastMileage: parseInt(data.lastMileage) || null,
			startingMileage: parseInt(data.startingMileage) || null,

			purchaseDate: data.purchaseDate
				? moment(data.purchaseDate, "MM/DD/YYYY").format("YYYY-MM-DD")
				: null,
			originalCost: data.originalCost || null,
			purchasedFrom: data.purchasedFrom?.trim() || null,
			fundingSourceId: data.fundingSourceId || null,

			licenseNumber: data.licenseNumber?.trim() || null,
			licenseExpiration: data.licenseExpiration || null,

			serviceRequirements: data.serviceRequirements || [],

			agencyId: user?.userAgency || null,
		};

		onSave(payload);
		reset();
		onClose();
	};

	// ── Service requirements helpers (managed inside watch) ──────────────────
	const assignedReqs = watch("serviceRequirements") || [];

	const addReq = (id) =>
		setValue("serviceRequirements", [...assignedReqs, id], {
			shouldDirty: true,
		});

	const removeReq = (id) =>
		setValue(
			"serviceRequirements",
			assignedReqs.filter((x) => x !== id),
			{ shouldDirty: true },
		);
	const activeReqs = serviceRequirements || [];
	const availableReqs = activeReqs.filter(
		(r) => !assignedReqs.includes(r?.Code),
	);

	// Mileage computed display
	const odometer = watch("odometer") || 0;
	const startingMileage = watch("startingMileage") || 0;
	const totalMiles = odometer - startingMileage;

	// License expiry check
	const licenseExpiration = watch("licenseExpiration");
	const licenseExpired =
		licenseExpiration && new Date(licenseExpiration + "-01") < new Date();

	const dialogTitle = initialData
		? viewOnly
			? `View Vehicle — ${initialData.unitNumber}`
			: `Edit Vehicle — ${initialData.unitNumber}`
		: "Add New Vehicle";

	const generateCustomId = async () => {
		const data = {
			EntityName: entityNamesForCustomId.FleetId,
			agencyID: userAgency,
		};
		const service = getService(serviceNames.customId);
		service.timeout = 20000;

		const id = await service.create(data);
		if (id?.includes("Agency ID does not match")) {
			dispatch(
				notify(
					"Unable to generate Vehicle ID because the Agency ID does not match.",
					"warning",
				),
			);
			return null;
		}
		return id;
	};

	useEffect(() => {
		setValue("vehicleNum", "");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userAgency]);

	const handleAutoGenerateClick = async () => {
		const id = await generateCustomId();
		setValue("vehicleNum", id, { shouldValidate: true });
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle>{dialogTitle}</DialogTitle>

			<Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
				<Tabs
					value={tab}
					onChange={(_, v) => setTab(v)}
					variant="scrollable"
					scrollButtons="auto"
				>
					<Tab label="Vehicle Info" />
					<Tab label="Mileage" />
					<Tab label="Acquisition" />
					<Tab label="Registration" />
					<Tab label="Service Req." />
				</Tabs>
			</Box>

			<DialogContent
				sx={{
					pointerEvents: viewOnly ? "none" : undefined,
					opacity: viewOnly ? 0.75 : undefined,
				}}
			>
				<Box
					component="form"
					onSubmit={handleSubmit(onSubmit)}
					noValidate
					id="VehicleForm"
				>
					{/* ── Vehicle Info ── */}
					<TabPanel value={tab} index={0}>
						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: "repeat(3, 1fr)",
								gap: 2,
							}}
						>
							<Box display="flex" alignItems="center">
								<Box flexGrow={1}>
									<TextFieldController
										name="vehicleNum"
										label="Vehicle#"
										required={true}
										control={control}
										errors={errors}
									/>
								</Box>
								<Box>
									<Tooltip title="Auto generate vehicle number">
										<IconButton
											color="primary"
											aria-label="Auto generate vehicle number"
											size="large"
											onClick={handleAutoGenerateClick}
										>
											<AssignmentReturn />
										</IconButton>
									</Tooltip>
								</Box>
							</Box>
							<Box>
								<TextFieldController
									name="unitNumber"
									label="Unit Number"
									control={control}
									errors={errors}
									fullWidth
								/>
							</Box>
							<Box>
								<TextFieldController
									name="inventoryNumber"
									label="Inventory Number"
									control={control}
									errors={errors}
								/>
							</Box>
							<Box>
								<DropdownController
									name="status"
									label="Status"
									options={statusOptions || []}
									control={control}
									errors={errors}
								/>
							</Box>
							<Box sx={{ gridColumn: "1 / -1" }}>
								<TextFieldController
									name="vin"
									label="VIN"
									control={control}
									errors={errors}
								/>
							</Box>
							<Box>
								<TextFieldController
									name="make"
									label="Make"
									control={control}
									errors={errors}
								/>
							</Box>
							<Box>
								<TextFieldController
									name="model"
									label="Model"
									control={control}
									errors={errors}
								/>
							</Box>
							<Box>
								<TextFieldController
									name="year"
									label="Year"
									control={control}
									errors={errors}
									type="number"
								/>
							</Box>
							<Box>
								<DropdownController
									name="division"
									label="Division"
									options={divisionOptions || []}
									control={control}
									errors={errors}
								/>
							</Box>
							<Box>
								<DropdownController
									name="perPersonnelId"
									label="Assigned Employee"
									options={employeeOptions || []}
									control={control}
									errors={errors}
									placeholder="— Unassigned —"
								/>
							</Box>
							<Box sx={{ gridColumn: "1 / -1" }}>
								<TextFieldController
									name="notes"
									label="Notes"
									control={control}
									errors={errors}
									multiline
									rows={2}
								/>
							</Box>
						</Box>
					</TabPanel>

					{/* ── Mileage ── */}
					<TabPanel value={tab} index={1}>
						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: "repeat(3, 1fr)",
								gap: 2,
							}}
						>
							<Box>
								<TextFieldController
									name="odometer"
									label="Current Odometer (mi)"
									control={control}
									errors={errors}
									type="number"
								/>
							</Box>
							<Box>
								<TextFieldController
									name="lastMileage"
									label="Last Mileage (mi)"
									control={control}
									errors={errors}
									type="number"
								/>
							</Box>
							<Box>
								<TextFieldController
									name="startingMileage"
									label="Starting Mileage (mi)"
									control={control}
									errors={errors}
									type="number"
								/>
							</Box>

							{odometer || startingMileage ? (
								<Box
									sx={{
										gridColumn: "1 / -1",
										p: 2,
										border: "1px solid",
										borderColor: "divider",
										borderRadius: 1,
										display: "flex",
										alignItems: "center",
										gap: 2,
									}}
								>
									<Typography
										variant="caption"
										fontWeight={600}
										color="text.secondary"
									>
										Total Miles
									</Typography>
									<Typography
										variant="body1"
										fontWeight={700}
										color={
											totalMiles > 150000
												? "error.main"
												: "success.main"
										}
										fontFamily="monospace"
									>
										{totalMiles.toLocaleString()} mi
									</Typography>
									{totalMiles > 150000 && (
										<Typography
											variant="caption"
											fontWeight={700}
											color="error.main"
										>
											⚠ High mileage
										</Typography>
									)}
								</Box>
							) : null}
						</Box>
					</TabPanel>

					{/* ── Acquisition ── */}
					<TabPanel value={tab} index={2}>
						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: "repeat(2, 1fr)",
								gap: 2,
							}}
						>
							<Box>
								<DatePickerController
									name="purchaseDate"
									label="Purchase Date"
									control={control}
									errors={errors}
								/>
							</Box>
							<Box>
								<TextFieldController
									name="originalCost"
									label="Original Cost ($)"
									control={control}
									errors={errors}
									type="number"
								/>
							</Box>
							<Box sx={{ gridColumn: "1 / -1" }}>
								<TextFieldController
									name="purchasedFrom"
									label="Purchased / Acquired From"
									control={control}
									errors={errors}
								/>
							</Box>
							<Box sx={{ gridColumn: "1 / -1" }}>
								<DropdownController
									name="fundingSourceId"
									label="Funding Source"
									options={fundingSourceOptions || []}
									control={control}
									errors={errors}
								/>
							</Box>
						</Box>
					</TabPanel>

					{/* ── Registration ── */}
					<TabPanel value={tab} index={3}>
						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: "repeat(2, 1fr)",
								gap: 2,
							}}
						>
							<Box>
								<TextFieldController
									name="licenseNumber"
									label="License Number"
									control={control}
									errors={errors}
								/>
							</Box>
							<Box>
								<TextFieldController
									name="licenseExpiration"
									label="License Expiration (YYYY-MM)"
									control={control}
									errors={errors}
								/>
								{licenseExpired && (
									<Typography
										variant="caption"
										color="error"
										fontWeight={700}
										sx={{ mt: 0.5, display: "block" }}
									>
										⚠ License is expired
									</Typography>
								)}
							</Box>
						</Box>
					</TabPanel>

					{/* ── Service Requirements ── */}
					<TabPanel value={tab} index={4}>
						{/* Assigned */}
						{assignedReqs.length === 0 ? (
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ textAlign: "center", py: 3 }}
							>
								No service requirements assigned. Use the list
								below to add.
							</Typography>
						) : (
							<Box sx={{ mb: 2 }}>
								<Typography
									variant="caption"
									fontWeight={700}
									color="text.secondary"
									sx={{
										textTransform: "uppercase",
										letterSpacing: "0.06em",
										mb: 1,
										display: "block",
									}}
								>
									Assigned Requirements
								</Typography>
								{assignedReqs.map((Code) => {
									const req = activeReqs.find(
										(r) => r.Code === Code,
									);
									if (!req) return null;
									return (
										<Box
											key={req.ptsCodeId}
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 1.5,
												p: "10px 14px",
												border: "1px solid",
												borderColor: "divider",
												borderRadius: 1,
												mb: 1,
											}}
										>
											<Box sx={{ flex: 1 }}>
												<Typography
													variant="body2"
													fontWeight={600}
												>
													{req?.Description || ""}
												</Typography>
												<Typography
													variant="caption"
													color="text.secondary"
													fontFamily="monospace"
												>
													Every{" "}
													{Number(
														req?.Value,
													).toLocaleString()}{" "}
													{req?.Unit}
												</Typography>
											</Box>
											<PATCloseButton
												onClick={() => removeReq(Code)}
												tooltipText="Remove"
												size="small"
											/>
										</Box>
									);
								})}
							</Box>
						)}

						{/* Available to add */}
						{availableReqs.length > 0 && (
							<Box>
								<Typography
									variant="caption"
									fontWeight={700}
									color="text.secondary"
									sx={{
										textTransform: "uppercase",
										letterSpacing: "0.06em",
										mb: 1,
										display: "block",
									}}
								>
									Add Requirement
								</Typography>
								{availableReqs.map((req) => (
									<Box
										key={req.ptsCodeId}
										onClick={() => addReq(req.Code)}
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 1.5,
											p: "8px 14px",
											border: "1px solid",
											borderColor: "divider",
											borderRadius: 1,
											mb: 0.75,
											cursor: "pointer",
											"&:hover": {
												borderColor: "primary.main",
											},
										}}
									>
										<Box sx={{ flex: 1 }}>
											<Typography
												variant="body2"
												fontWeight={600}
											>
												{req?.Description || ""}
											</Typography>
											<Typography
												variant="caption"
												color="text.secondary"
												fontFamily="monospace"
											>
												Every{" "}
												{Number(
													req?.Value,
												).toLocaleString()}{" "}
												{req.Unit}
											</Typography>
										</Box>
										<PATAddButton
											onClick={(e) => {
												e.stopPropagation();
												addReq(req?.Code);
											}}
											tooltipText="Add"
											size="small"
										/>
									</Box>
								))}
							</Box>
						)}

						{availableReqs.length === 0 &&
							assignedReqs.length > 0 && (
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{
										textAlign: "center",
										display: "block",
										pt: 1,
									}}
								>
									All available service requirements are
									assigned.
								</Typography>
							)}
					</TabPanel>
				</Box>
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2 }}>
				{!viewOnly && (
					<PATSaveButton
						onClick={() => {
							handleSubmit(onSubmit)();
						}}
						tooltipText="Save"
						disabled={!isValid}
					/>
				)}
				<PATCloseButton onClick={onClose} tooltipText="Close" />
			</DialogActions>
		</Dialog>
	);
};

export default AddEditVehicleDialog;
